import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CListGroup,
  CListGroupItem,
  CRow,
} from "@coreui/react";
import { useBacktests } from "@src/hooks/redux";
import type { AppOptimizationStudyResult, CommonSettingsData } from "@src/types";
import { formatBacktestDataFromSource } from "@src/utils/formatters";

import { ColoredValue } from "@src/components/ColoredValue";
import { StudyRobustness } from "@src/components/StudyRobustness";
import { StudyTopCandidates } from "@src/components/StudyTopCandidates";
import { ResultsStudyTrialsTable } from "./ResultsStudyTrialsTable";

interface Props {
  optResults: AppOptimizationStudyResult;
  optSettings: CommonSettingsData;
  optimizationJobId: number;
}

export const ResultsStudyBlock: React.FC<Props> = ({
  optResults,
  optSettings,
  optimizationJobId,
}) => {
  // Initialize with the best trial number directly to avoid extra render
  const [selectedTrialNumber, setSelectedTrialNumber] = useState<number>(
    () => optResults.best_trial.trial_number,
  );
  const { setBacktestFromExternal } = useBacktests();
  const reactRouterNavigateTo = useNavigate();

  // optimization_metric now carries the study's real objective. Lower-is-better objectives (drawdown,
  // kurtosis, loss signals) must not be colored positive-green; color the higher-is-better ones.
  const objectiveName = optResults.summary.optimization_metric;
  const LOWER_IS_BETTER_OBJECTIVES = new Set(["kurtosis", "loss_signals"]);
  const isLowerIsBetter =
    objectiveName.toLowerCase().includes("drawdown") ||
    LOWER_IS_BETTER_OBJECTIVES.has(objectiveName);

  // Limit dropdown to top 100 trials to avoid rendering 7000+ DOM nodes which causes 5s delays
  const topTrials = React.useMemo(() => {
    const top100 = [...optResults.all_trials]
      .sort((a, b) => b.metrics.cumulative_returns - a.metrics.cumulative_returns)
      .slice(0, 100);
    return top100;
  }, [optResults.all_trials]);

  const selectedTrial = optResults.all_trials.find(
    ({ trial_number: tn }) => tn === selectedTrialNumber,
  );

  const handleRunBacktest = () => {
    if (!selectedTrial) {
      console.error("No trial selected");
      return;
    }

    console.log(
      `[Trial→Backtest] Converting trial #${selectedTrial.trial_number} to backtest (from job #${optimizationJobId})`,
    );

    // Use new field names (v0.7.30+)
    // Include cost_config when available (persisted since optimizer v0.3.4+)
    const combinedConfig = JSON.stringify(
      {
        strategy_config: selectedTrial.strategy_config,
        exit_config: selectedTrial.exit_config,
        ...(selectedTrial.regime_config && { regime_config: selectedTrial.regime_config }),
        ...(selectedTrial.cost_config && { cost_config: selectedTrial.cost_config }),
      },
      null,
      2,
    );

    const backtestData: CommonSettingsData & { config: string } = {
      strategyId: optSettings.strategyId,
      exchangeName: optSettings.exchangeName,
      instrument: optSettings.instrument,
      timeframe: optSettings.timeframe,
      startDateTime: optSettings.startDateTime,
      endDateTime: optSettings.endDateTime,
      investmentAmount: optSettings.investmentAmount,
      cumulativeInvestment: optSettings.cumulativeInvestment,
      config: combinedConfig,
      // Include origin metadata for traceability
      origin: {
        type: "optimization_trial",
        optimization_job_id: optimizationJobId,
        trial_number: selectedTrial.trial_number,
        study_id: optResults.study_name,
      },
    };

    setBacktestFromExternal(formatBacktestDataFromSource(backtestData));
    reactRouterNavigateTo("/backtest/new?loadFrom=optimization");
  };

  return (
    <div className="ResultsStudyBlock">
      <CRow className="mb-4">
        <CCol md={6} className="OptResultsSummaryCol">
          <CCard>
            <CCardHeader className="OptResultsSummaryHeader">
              <h6 className="mb-0">Optimization Summary</h6>
            </CCardHeader>
            <CCardBody>
              <CRow className="mb-2">
                <CCol xs={6}>
                  <strong>Study:</strong>
                </CCol>
                <CCol xs={6}>
                  {optResults.study_name}{" "}
                  <a href={optResults.study_url} target="_blank" rel="noopener noreferrer">
                    [Link]
                  </a>
                </CCol>
              </CRow>
              <CRow className="mb-2">
                <CCol xs={6}>
                  <strong>Total Trials:</strong>
                </CCol>
                <CCol xs={6}>{optResults.summary.metrics_summary.total_trials_run}</CCol>
              </CRow>
              <CRow className="mb-2">
                <CCol xs={6}>
                  <strong>Best Trial:</strong>
                </CCol>
                <CCol xs={6}>#{optResults.best_trial.trial_number}</CCol>
              </CRow>
              <CRow>
                <CCol xs={6}>
                  {/* Label by the real objective; color only higher-is-better objectives. */}
                  <strong>
                    Best {objectiveName ? objectiveName.replace(/_/g, " ") : "objective"}:
                  </strong>
                </CCol>
                <CCol xs={6}>
                  <ColoredValue
                    value={optResults.best_trial.value}
                    threshold={isLowerIsBetter ? undefined : 0}
                  />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md={6} className="BestPerformanceMetricsCol">
          <CCard>
            <CCardHeader className="BestPerformanceMetricsHeader">
              <h6 className="mb-0">Best Performance Metrics</h6>
            </CCardHeader>
            <CCardBody style={{ paddingRight: "20%" }}>
              <div className="d-flex justify-content-between mb-2">
                <strong>Best Win Rate:</strong>
                <ColoredValue
                  value={optResults.summary.metrics_summary.best_win_rate * 100}
                  type="percentage"
                />
              </div>
              <div className="d-flex justify-content-between mb-2">
                <strong>Best Profit Factor:</strong>
                <ColoredValue value={optResults.summary.metrics_summary.best_profit_factor} />
              </div>
              <div className="d-flex justify-content-between mb-2">
                <strong>Best Sortino Ratio:</strong>
                <ColoredValue value={optResults.summary.metrics_summary.best_sortino_ratio} />
              </div>
              <div className="d-flex justify-content-between">
                <strong>Best Sharpe Ratio:</strong>
                <ColoredValue value={optResults.summary.metrics_summary.best_sharpe_ratio} />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <StudyRobustness robustness={optResults.robustness} />

      <StudyTopCandidates studyId={optResults.study_id} />

      <ResultsStudyTrialsTable
        trials={optResults.all_trials}
        bestTrialNumber={optResults.best_trial.trial_number}
      />

      <CCard>
        <CCardHeader className="TrialSelectionHeader">
          <h6 className="mb-0">Exec Trial</h6>
          {optResults.all_trials.length > 100 && (
            <small className="text-muted">(Showing top 100 by performance)</small>
          )}
        </CCardHeader>
        <CCardBody className="TrialSelectionBody">
          <CRow>
            <CCol md={2}>
              <h6 className="mb-2">Trial Selection</h6>
              <CListGroup style={{ maxHeight: "400px", overflowY: "auto" }}>
                {topTrials.map((trial) => (
                  <CListGroupItem
                    key={trial.trial_number}
                    as="button"
                    onClick={() => setSelectedTrialNumber(trial.trial_number)}
                    active={selectedTrialNumber === trial.trial_number}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span className="ColoredValue" style={{ minWidth: "60px" }}>
                      #{trial.trial_number}
                    </span>
                    <ColoredValue
                      value={trial.metrics.cumulative_returns}
                      badge
                      size="s"
                      threshold={0}
                    />
                  </CListGroupItem>
                ))}
              </CListGroup>
            </CCol>

            {selectedTrial && (
              <>
                <CCol md={3}>
                  <CCard>
                    <CCardHeader className="py-1 StrategyParametersHeader">
                      <small className="mb-0">Strategy Parameters</small>
                    </CCardHeader>
                    <CCardBody className="p-2">
                      {/* FIX(branch): sort keys alphabetically for side-by-side trial comparison */}
                      <pre style={{ fontSize: "0.8rem", marginBottom: 0 }}>
                        {JSON.stringify(
                          selectedTrial.strategy_config,
                          Object.keys(selectedTrial.strategy_config).sort(),
                          2,
                        )}
                      </pre>
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol md={4}>
                  <CCard>
                    <CCardHeader className="py-1 ExitParametersHeader">
                      <small className="mb-0">Exit Parameters</small>
                    </CCardHeader>
                    <CCardBody className="p-2">
                      <pre style={{ fontSize: "0.8rem", marginBottom: 0 }}>
                        {JSON.stringify(
                          selectedTrial.exit_config,
                          Object.keys(selectedTrial.exit_config).sort(),
                          2,
                        )}
                      </pre>
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol md={3}>
                  <CCard>
                    <CCardHeader className="py-1 TrialMetricsHeader">
                      <small className="mb-0">Trial Metrics</small>
                    </CCardHeader>
                    <CCardBody className="p-2">
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Cumulative Return:</strong>
                        <ColoredValue
                          value={selectedTrial.metrics.cumulative_returns}
                          threshold={0}
                        />
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Win Rate:</strong>
                        <ColoredValue
                          value={selectedTrial.metrics.win_rate * 100}
                          type="percentage"
                        />
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Profit Factor:</strong>
                        <ColoredValue value={selectedTrial.metrics.profit_factor} />
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Sortino Ratio:</strong>
                        <ColoredValue value={selectedTrial.metrics.sortino_ratio} />
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Sharpe Ratio:</strong>
                        <ColoredValue value={selectedTrial.metrics.sharpe_ratio} />
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Max Drawdown:</strong>
                        <ColoredValue
                          value={selectedTrial.metrics.max_drawdown_pct}
                          type="percentage"
                        />
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Total Positions:</strong>
                        <ColoredValue value={selectedTrial.metrics.total_positions} type="flat" />
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Win Signals:</strong>
                        <ColoredValue value={selectedTrial.metrics.win_signals} type="flat" />
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <strong>Loss Signals:</strong>
                        <ColoredValue value={selectedTrial.metrics.loss_signals} type="flat" />
                      </div>
                    </CCardBody>
                  </CCard>

                  <div className="mt-3">
                    <CButton
                      color="primary"
                      className="w-100"
                      onClick={handleRunBacktest}
                      disabled={!selectedTrial || !optSettings}
                    >
                      Run Backtest
                      <br />
                      Trial #{selectedTrialNumber}
                    </CButton>
                  </div>
                </CCol>
              </>
            )}
          </CRow>
        </CCardBody>
      </CCard>
    </div>
  );
};
